import type { TreeOption } from "naive-ui";
import { buildResultBodyDisplayNameMap, resolveModelBodyIdentity } from "@puppycad/cad-core";
import type { GeometryObject } from "@puppycad/cad-core";
import { FeatureTreeNodeKind } from "@/hooks/feature-tree/feature-tree-types";
import type { FeatureTreeNodeMeta } from "@/hooks/feature-tree/feature-tree-types";
import { FeatureTreePresentationIndex } from "./FeatureTreePresentationIndex";
import { resolveUniqueBodyTreeNodeLabel } from "@/utils/feature-tree";

/**
 * 将原生对象名称变化投影到全部出现项，保留 UI 树结构与选择状态。
 * 结果体后缀和显式覆盖优先级继续由 SDK 命名策略决定；本类只维护 UI 身份索引。
 */
export class FeatureTreeNameProjectionIndex {
	/** 当前结构树引用；换树必须解除旧节点绑定。 */
	private tree: readonly TreeOption[] | null = null;
	/** 当前权威 Worker 对象快照，变化时重新建立来源特征分组。 */
	private objects: readonly GeometryObject[] | null = null;
	/** 原生业务身份对应的全部出现项，不混用草图、特征与基准的 ID。 */
	private readonly keysByIdentity = new Map<string, string[]>();
	/** 结果体可能与特征使用相同 objectId，必须单独索引才能正确追加序号。 */
	private readonly bodyKeysById = new Map<string, string[]>();
	/** 当前特征的代表节点，用于冷态建立完整原始名称，不扫描无关装配节点。 */
	private readonly featureKeysById = new Map<string, string>();
	/** 未经过文件夹重名处理的名称，禁止从已经加后缀的 UI label 反推。 */
	private readonly rawBodyLabels = new Map<string, string>();
	/** 存在外部实体合并的文件夹才使用完整投影已有的去重规则。 */
	private mixedBodyChildren: readonly TreeOption[] | null = null;
	/** 每个特征的完整结果体分组，排序和名称仍委托 SDK。 */
	private readonly bodiesByFeature = new Map<string, GeometryObject[]>();
	/** Worker 对象 ID 与统一实体修订的映射，沿用 SDK 身份规则处理离线表示。 */
	private readonly bodyRevisionByObjectId = new Map<string, string>();
	/** 共用稳定 key 定位，不在每次名称变化遍历整棵树。 */
	private readonly nodes = new FeatureTreePresentationIndex();
	/** 累计身份索引构建次数，仅在调试读取。 */
	private builds = 0;

	/**
	 * 应用一次名称变更；不支持的 GLB/系统身份明确返回 false，由宿主保留原结构刷新路径。
	 * @param tree 当前纯 UI 树。
	 * @param nodeMap 树对应的语义元数据。
	 * @param objects 对应的 Worker 对象快照。
	 * @param change 已完成业务写入的名称变化。
	 * @param overrides 既有名称覆盖表，结果体显式命名优先级不得被特征名覆盖。
	 * @returns 身份可由当前投影定位时返回 true。
	 */
	public apply(
		tree: readonly TreeOption[],
		nodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>,
		objects: readonly GeometryObject[],
		change: FeatureTreeNameChange,
		overrides: Map<string, string>
	): boolean {
		const identity = this.resolveIdentity(change.target);
		const current = nodeMap.get(change.key);
		if (!identity || !current || identity !== this.resolveIdentity(current)) return false;
		if (this.tree !== tree) {
			this.clear();
			this.tree = tree;
			let hasExternalBodies = false;
			nodeMap.forEach((meta, key) => {
				const isBody =
					(meta.kind === FeatureTreeNodeKind.Object || meta.kind === FeatureTreeNodeKind.GlbNode) &&
					(meta.objectType === "solid" || meta.objectType === "body");
				if (isBody) {
					const bodyId = meta.modelBodyRevisionId || meta.objectId;
					if (bodyId) {
						const bodyKeys = this.bodyKeysById.get(bodyId) ?? [];
						bodyKeys.push(key);
						this.bodyKeysById.set(bodyId, bodyKeys);
					}
					if (meta.kind === FeatureTreeNodeKind.GlbNode) hasExternalBodies = true;
				}
				const nodeIdentity = this.resolveIdentity(meta);
				if (!nodeIdentity) return;
				const keys = this.keysByIdentity.get(nodeIdentity) ?? [];
				keys.push(key);
				this.keysByIdentity.set(nodeIdentity, keys);
				if (meta.objectType === "feature" && meta.objectId) this.featureKeysById.set(meta.objectId, key);
			});
			if (hasExternalBodies) this.mixedBodyChildren = this.nodes.resolve(tree, "solid-bodies-folder")?.children ?? null;
			this.builds += 1;
		}
		if (this.objects !== objects) {
			this.objects = objects;
			this.bodiesByFeature.clear();
			this.bodyRevisionByObjectId.clear();
			for (const object of objects) {
				const bodyIdentity = resolveModelBodyIdentity(object);
				if (bodyIdentity) this.bodyRevisionByObjectId.set(object.id, bodyIdentity.modelBodyRevisionId);
				const featureId = object.metadata?.featureId;
				if (object.type !== "solid" || typeof featureId !== "string") continue;
				const bodies = this.bodiesByFeature.get(featureId) ?? [];
				bodies.push(object);
				this.bodiesByFeature.set(featureId, bodies);
			}
			// 冷态只读取当前特征标题和权威对象，委托 SDK 恢复未去重的结果体名。
			const features = [...this.featureKeysById].map(([id, key]) => ({
				id,
				kind: nodeMap.get(key)?.featureKind ?? "",
				displayName: String(this.nodes.resolve(tree, key)?.label ?? id),
			}));
			const bodyNames = buildResultBodyDisplayNameMap({ objects: [...objects], features, objectNameOverrides: overrides });
			const namesByRevision = new Map<string, string>();
			bodyNames.forEach((label, id) => namesByRevision.set(this.bodyRevisionByObjectId.get(id) ?? id, label));
			this.rawBodyLabels.clear();
			this.bodyKeysById.forEach((keys, bodyId) => {
				for (const key of keys) {
					const meta = nodeMap.get(key)!;
					const node = this.nodes.resolve(tree, key);
					const nativeName = meta.kind === FeatureTreeNodeKind.Object || meta.createdByFeatureId ? namesByRevision.get(bodyId) : undefined;
					this.rawBodyLabels.set(key, nativeName ?? meta.displayName ?? String(node?.label ?? "实体"));
				}
			});
		}
		const name = change.name.trim();
		let changedBodies = false;
		for (const key of this.keysByIdentity.get(identity) ?? []) {
			const node = this.nodes.resolve(tree, key);
			if (node) node.label = name;
			if (this.rawBodyLabels.has(key)) {
				this.rawBodyLabels.set(key, name);
				changedBodies = true;
			}
		}
		if (change.target.objectType === "feature" && change.target.objectId) {
			const bodies = this.bodiesByFeature.get(change.target.objectId) ?? [];
			const names = buildResultBodyDisplayNameMap({
				objects: bodies,
				objectNameOverrides: overrides,
				features: [{ id: change.target.objectId, kind: change.target.featureKind ?? "", displayName: name }],
			});
			names.forEach((displayName, bodyId) => {
				for (const key of this.bodyKeysById.get(this.bodyRevisionByObjectId.get(bodyId) ?? bodyId) ?? []) {
					const node = this.nodes.resolve(tree, key);
					if (node) node.label = displayName;
					this.rawBodyLabels.set(key, displayName);
					const meta = nodeMap.get(key);
					if (meta?.kind === FeatureTreeNodeKind.GlbNode && meta.createdByFeatureId) meta.displayName = displayName;
					changedBodies = true;
				}
			});
		}
		if (changedBodies && this.mixedBodyChildren) {
			// 重名会影响同一文件夹的其它后缀，只重算该依赖范围，保持所有节点/DOM 引用。
			const usedLabels = new Set<string>();
			for (const node of this.mixedBodyChildren) {
				const rawLabel = this.rawBodyLabels.get(String(node.key)) ?? String(node.label ?? "实体");
				node.label = resolveUniqueBodyTreeNodeLabel(rawLabel, usedLabels);
			}
		}
		return true;
	}

	/** @returns 不在名称更新热路径分配的显式索引统计。 */
	public getStats(): FeatureTreePresentationIndexStats {
		return { builds: this.builds, nodes: this.nodes.getStats().nodes };
	}

	/** 清理结构/文档边界的全部引用，不修改调用方树数据。 */
	public clear(): void {
		this.tree = null;
		this.objects = null;
		this.keysByIdentity.clear();
		this.bodyKeysById.clear();
		this.featureKeysById.clear();
		this.rawBodyLabels.clear();
		this.mixedBodyChildren = null;
		this.bodiesByFeature.clear();
		this.bodyRevisionByObjectId.clear();
		this.nodes.clear();
	}

	/** @param meta 只读业务身份。 @returns 可增量命名的原生身份，外部层级不在此处猜测名称。 */
	private resolveIdentity(meta: FeatureTreeNameChange["target"]): string | null {
		if (meta.kind === FeatureTreeNodeKind.DatumPlane && meta.planeId !== undefined) return `${meta.kind}:${meta.planeId}`;
		if (meta.kind === FeatureTreeNodeKind.DatumAxis && meta.axisId !== undefined) return `${meta.kind}:${meta.axisId}`;
		if (meta.kind === FeatureTreeNodeKind.DatumPoint && meta.pointId !== undefined) return `${meta.kind}:${meta.pointId}`;
		if (
			meta.kind === FeatureTreeNodeKind.Object &&
			meta.objectId &&
			(meta.objectType === "feature" || meta.objectType === "sketch" || meta.objectType === "solid" || meta.objectType === "body")
		) {
			return `${meta.objectType}:${meta.objectId}`;
		}
		return null;
	}
}
